// SPDX-License-Identifier: GPL-3.0-or-later
//
// Full-screen modal for reviewing & editing a generated Typst document:
//   - Left pane: the raw .typ source in a textarea (manual edits).
//   - Right pane: AI prompt panel that proposes find/replace patches with the
//     job description, profile and current source in context.
//
// The editor saves edits back to the original .typ on disk and re-compiles
// the PDF in place, preserving the manifest's generated reference so the meta
// line on JobDetail still points at the same files.

import { readJobTxt } from '../fs/jobtxt';
import { readProfile } from '../fs/profile';
import { applyPatch, requestPatch, type AppliedEdit, type DocKind } from '../ai/patch';
import { setGeneratedRef } from '../store/applications';
import { compileTypstToPdf } from '../typst/engine';
import type { Application, GenRef } from '../types';
import { getWalletState } from '../wallet';
import { el } from './dom';

export interface DocEditorOptions {
  app: Application;
  kind: DocKind;
  root: FileSystemDirectoryHandle;
  texRelPath: string;
  pdfRelPath?: string;
  onSaved?: () => void;
}

const KIND_TITLE: Record<DocKind, string> = {
  cv: 'Tailored CV',
  cover: 'Cover letter',
  interview: 'Interview prep',
};

function lastLines(s: string, n: number): string {
  const lines = s.split('\n');
  return lines.slice(-n).join('\n');
}

async function resolveFolder(
  root: FileSystemDirectoryHandle,
  relPath: string,
): Promise<FileSystemDirectoryHandle | null> {
  const parts = relPath.split('/').filter(Boolean);
  let cur: FileSystemDirectoryHandle = root;
  for (const seg of parts) {
    try { cur = await cur.getDirectoryHandle(seg); }
    catch { return null; }
  }
  return cur;
}

async function readTextFile(
  root: FileSystemDirectoryHandle,
  relPath: string,
): Promise<string | null> {
  const parts = relPath.split('/').filter(Boolean);
  const name = parts.pop();
  if (!name) return null;
  const folder = await resolveFolder(root, parts.join('/'));
  if (!folder) return null;
  try {
    const handle = await folder.getFileHandle(name);
    return (await handle.getFile()).text();
  } catch {
    return null;
  }
}

async function readBinaryFile(
  root: FileSystemDirectoryHandle,
  relPath: string,
): Promise<Uint8Array | null> {
  const parts = relPath.split('/').filter(Boolean);
  const name = parts.pop();
  if (!name) return null;
  const folder = await resolveFolder(root, parts.join('/'));
  if (!folder) return null;
  try {
    const handle = await folder.getFileHandle(name);
    const file = await handle.getFile();
    return new Uint8Array(await file.arrayBuffer());
  } catch {
    return null;
  }
}

async function writeTextFile(
  dir: FileSystemDirectoryHandle,
  name: string,
  contents: string,
): Promise<void> {
  const handle = await dir.getFileHandle(name, { create: true });
  const writable = await handle.createWritable();
  try { await writable.write(contents); }
  finally { await writable.close(); }
}

async function writeBinaryFile(
  dir: FileSystemDirectoryHandle,
  name: string,
  bytes: Uint8Array,
): Promise<void> {
  const handle = await dir.getFileHandle(name, { create: true });
  const writable = await handle.createWritable();
  try { await writable.write(new Blob([new Uint8Array(bytes)])); }
  finally { await writable.close(); }
}

function splitRelPath(rel: string): { dirRel: string; name: string } | null {
  const parts = rel.split('/').filter(Boolean);
  const name = parts.pop();
  if (!name) return null;
  return { dirRel: parts.join('/'), name };
}

interface PatchCardOpts {
  edit: AppliedEdit;
  index: number;
  onUndo: (index: number) => void;
}

function renderPatchCard({ edit, index, onUndo }: PatchCardOpts): HTMLElement {
  const statusClass = edit.applied ? 'applied' : 'skipped';
  const statusLabel = edit.applied ? 'Applied' : `Skipped — ${edit.reason ?? 'unknown'}`;

  const header = el(
    'div',
    { class: 'patch-card-head' },
    el('span', { class: `patch-status ${statusClass}` }, statusLabel),
    edit.applied
      ? el(
          'button',
          {
            class: 'btn tiny',
            title: 'Revert this single edit',
            onclick: () => onUndo(index),
          },
          'Undo',
        )
      : null,
  );

  const desc = edit.description
    ? el('div', { class: 'patch-desc' }, edit.description)
    : null;

  const diffEl = el(
    'div',
    { class: 'patch-diff' },
    el('pre', { class: 'patch-find' }, edit.find),
    el('pre', { class: 'patch-replace' }, edit.replace),
  );

  return el('div', { class: 'patch-card' }, header, desc, diffEl);
}

export function openDocEditor(opts: DocEditorOptions): void {
  const { app, kind, root, texRelPath } = opts;

  let originalSource = '';
  let currentSource = '';
  let saving = false;

  const overlay = el('div', { class: 'doc-editor-overlay' });
  const dialog = el('div', { class: 'doc-editor-dialog', role: 'dialog', 'aria-modal': 'true' });

  // ---- Header ----
  const titleEl = el(
    'div',
    { class: 'doc-editor-title' },
    el('span', { class: 'doc-editor-eyebrow' }, KIND_TITLE[kind]),
    el('h2', {}, `${app.jobTitle}`),
    el('div', { class: 'doc-editor-subtitle' }, app.companyName),
  );

  const closeBtn = el(
    'button',
    {
      class: 'btn ghost',
      title: 'Close (Esc)',
      onclick: () => attemptClose(),
    },
    '×',
  );

  const header = el(
    'header',
    { class: 'doc-editor-head' },
    titleEl,
    closeBtn,
  );

  // ---- Left pane: PDF preview (default) with the raw source behind a toggle ----
  const textarea = el('textarea', {
    class: 'doc-editor-textarea',
    spellcheck: false,
    autocomplete: 'off',
    autocorrect: 'off',
    autocapitalize: 'off',
  }) as HTMLTextAreaElement;

  const dirtyDot = el('span', { class: 'doc-editor-dirty-dot', title: 'Unsaved changes' });
  dirtyDot.style.display = 'none';

  const previewFrame = el('iframe', {
    class: 'doc-editor-preview',
    title: 'PDF preview',
  }) as HTMLIFrameElement;
  const previewEmpty = el(
    'div',
    { class: 'doc-editor-preview-empty' },
    'Preparing preview…',
  );
  const previewWrap = el('div', { class: 'doc-editor-preview-wrap' }, previewFrame, previewEmpty);
  const sourceWrap = el('div', { class: 'doc-editor-source-wrap' }, textarea);
  sourceWrap.style.display = 'none';

  const previewTab = el('button', { class: 'doc-editor-tab active', type: 'button' }, 'Preview') as HTMLButtonElement;
  const sourceTab = el('button', { class: 'doc-editor-tab', type: 'button' }, 'Source') as HTMLButtonElement;
  const refreshBtn = el(
    'button',
    { class: 'btn tiny', type: 'button', title: 'Recompile the preview from the current source' },
    'Refresh preview',
  ) as HTMLButtonElement;
  function setView(v: 'preview' | 'source'): void {
    previewTab.classList.toggle('active', v === 'preview');
    sourceTab.classList.toggle('active', v === 'source');
    previewWrap.style.display = v === 'preview' ? '' : 'none';
    sourceWrap.style.display = v === 'source' ? '' : 'none';
  }
  previewTab.addEventListener('click', () => setView('preview'));
  sourceTab.addEventListener('click', () => setView('source'));
  refreshBtn.addEventListener('click', () => { void refreshPreview(); });

  const leftPane = el(
    'div',
    { class: 'doc-editor-pane left' },
    el(
      'div',
      { class: 'doc-editor-pane-head' },
      el('div', { class: 'doc-editor-tabs' }, previewTab, sourceTab),
      dirtyDot,
      refreshBtn,
    ),
    previewWrap,
    sourceWrap,
  );

  // ---- PDF preview state ----
  let previewUrl: string | null = null;
  function setPreviewPdf(bytes: Uint8Array | null): void {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      previewUrl = null;
    }
    if (!bytes || bytes.length === 0) {
      previewFrame.removeAttribute('src');
      previewFrame.style.display = 'none';
      previewEmpty.style.display = '';
      return;
    }
    const blob = new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
    previewUrl = URL.createObjectURL(blob);
    previewFrame.src = previewUrl;
    previewFrame.style.display = '';
    previewEmpty.style.display = 'none';
  }

  function setPreviewMessage(...children: (Node | string)[]): void {
    previewEmpty.replaceChildren(
      ...children.map((c) => (typeof c === 'string' ? document.createTextNode(c) : c)),
    );
    previewEmpty.style.display = '';
    previewFrame.style.display = 'none';
  }

  let compilingPreview = false;
  async function refreshPreview(): Promise<void> {
    if (compilingPreview) return;
    compilingPreview = true;
    refreshBtn.disabled = true;
    if (!previewUrl) setPreviewMessage('Compiling preview…');
    footerStatus.textContent = 'Compiling preview…';
    try {
      const compile = await compileTypstToPdf(currentSource);
      if (compile.pdf && compile.status === 0) {
        setPreviewPdf(compile.pdf);
        footerStatus.textContent = 'Preview updated.';
      } else {
        setPreviewMessage(
          el('div', { class: 'doc-editor-preview-error-title' }, "Couldn't compile to PDF"),
          el(
            'div',
            { class: 'doc-editor-preview-error-hint' },
            'Open the Source tab to fix it. Typst reported:',
          ),
          el('pre', { class: 'doc-editor-preview-log' }, lastLines(compile.log, 40) || '(no log)'),
        );
        footerStatus.textContent = 'Preview compile failed.';
      }
    } catch (err) {
      setPreviewMessage(
        el('div', { class: 'doc-editor-preview-error-title' }, 'Preview error'),
        el(
          'pre',
          { class: 'doc-editor-preview-log' },
          err instanceof Error ? (err.stack ?? err.message) : String(err),
        ),
      );
      footerStatus.textContent = 'Preview error.';
    } finally {
      compilingPreview = false;
      refreshBtn.disabled = false;
    }
  }

  // ---- Right pane: AI prompt panel ----
  const walletConnected = !!getWalletState().connection;

  const promptInput = el('textarea', {
    class: 'doc-editor-prompt',
    placeholder: walletConnected
      ? 'e.g. "Lead the summary with the SaaS-platform leadership angle. Tighten the third experience bullet."'
      : 'Turn on AI in Settings to ask for edits.',
    rows: 4,
    spellcheck: true,
  }) as HTMLTextAreaElement;
  if (!walletConnected) promptInput.disabled = true;

  const applyBtn = el(
    'button',
    {
      class: 'btn primary compact',
      disabled: !walletConnected,
    },
    'Ask AI',
  ) as HTMLButtonElement;

  const aiStatus = el('div', { class: 'doc-editor-ai-status' });
  const patchList = el('div', { class: 'doc-editor-patches' });

  const promptInputs = el(
    'div',
    { class: 'doc-editor-prompt-row' },
    promptInput,
    el('div', { class: 'doc-editor-prompt-actions' }, applyBtn),
  );

  const rightPane = el(
    'div',
    { class: 'doc-editor-pane right' },
    el(
      'div',
      { class: 'doc-editor-pane-head' },
      el('span', { class: 'doc-editor-pane-label' }, 'AI change request'),
    ),
    el(
      'div',
      { class: 'doc-editor-ai-hint' },
      'The model sees the current document, the job description, and your profile. It returns edits you can review and undo one at a time — the preview recompiles after each change.',
    ),
    promptInputs,
    aiStatus,
    patchList,
  );

  // ---- Body ----
  const body = el('div', { class: 'doc-editor-body' }, leftPane, rightPane);

  // ---- Footer ----
  const footerStatus = el('div', { class: 'doc-editor-footer-status' });

  const saveBtn = el(
    'button',
    { class: 'btn primary compact', disabled: true },
    'Save & compile',
  ) as HTMLButtonElement;
  const revertBtn = el(
    'button',
    { class: 'btn compact', disabled: true },
    'Revert',
  ) as HTMLButtonElement;
  const footer = el(
    'footer',
    { class: 'doc-editor-foot' },
    footerStatus,
    el('div', { class: 'doc-editor-foot-actions' }, revertBtn, saveBtn),
  );

  dialog.append(header, body, footer);
  overlay.append(dialog);

  // ---- State helpers ----
  function setDirty(): void {
    const dirty = textarea.value !== originalSource;
    currentSource = textarea.value;
    dirtyDot.style.display = dirty ? 'inline-block' : 'none';
    saveBtn.disabled = !dirty || saving;
    revertBtn.disabled = !dirty || saving;
  }

  textarea.addEventListener('input', setDirty);

  // ---- Loading ----
  textarea.value = 'Loading…';
  textarea.disabled = true;
  void (async () => {
    const text = await readTextFile(root, texRelPath);
    if (text === null) {
      textarea.value = '';
      textarea.disabled = false;
      footerStatus.textContent = `Could not read ${texRelPath} from disk.`;
      return;
    }
    originalSource = text;
    currentSource = text;
    textarea.value = text;
    textarea.disabled = false;
    setDirty();

    // Show the already-compiled PDF if there is one; otherwise compile from the
    // source so the preview isn't empty.
    let pdfBytes: Uint8Array | null = null;
    if (opts.pdfRelPath) pdfBytes = await readBinaryFile(root, opts.pdfRelPath);
    if (pdfBytes) setPreviewPdf(pdfBytes);
    else void refreshPreview();
  })();

  // ---- Save & compile ----
  async function saveAndCompile(): Promise<void> {
    if (saving) return;
    const split = splitRelPath(texRelPath);
    if (!split) {
      footerStatus.textContent = 'Invalid path.';
      return;
    }
    saving = true;
    saveBtn.disabled = true;
    revertBtn.disabled = true;
    footerStatus.textContent = 'Saving…';
    try {
      const folder = await resolveFolder(root, split.dirRel);
      if (!folder) {
        footerStatus.textContent = 'Job folder not found on disk.';
        return;
      }
      await writeTextFile(folder, split.name, currentSource);

      footerStatus.textContent = 'Compiling PDF…';
      let pdfRelPath: string | undefined;
      try {
        const compile = await compileTypstToPdf(currentSource);
        if (compile.pdf && compile.status === 0) {
          const baseName = split.name.replace(/\.typ$/i, '');
          const pdfName = `${baseName}.pdf`;
          await writeBinaryFile(folder, pdfName, compile.pdf);
          pdfRelPath = `${split.dirRel ? `${split.dirRel}/` : ''}${pdfName}`;
          setPreviewPdf(compile.pdf);
          footerStatus.textContent = `Saved ${split.name} + ${pdfName}.`;
        } else {
          footerStatus.textContent = `Saved ${split.name}. PDF compile failed; see the log in the doc page.`;
        }
      } catch (err) {
        footerStatus.textContent = `Saved ${split.name}, but compile threw: ${err instanceof Error ? err.message : String(err)}`;
      }

      const ref: GenRef = {
        texRelPath,
        generatedAt: new Date().toISOString(),
        ...(pdfRelPath ? { pdfRelPath } : {}),
      };
      try { await setGeneratedRef(app.id, kind, ref); }
      catch (err) {
        footerStatus.textContent = `Wrote files but manifest update failed: ${err instanceof Error ? err.message : String(err)}`;
      }

      originalSource = currentSource;
      setDirty();
      opts.onSaved?.();
    } catch (err) {
      footerStatus.textContent = `Save failed: ${err instanceof Error ? err.message : String(err)}`;
    } finally {
      saving = false;
      setDirty();
    }
  }

  saveBtn.addEventListener('click', () => { void saveAndCompile(); });

  revertBtn.addEventListener('click', () => {
    if (textarea.value === originalSource) return;
    if (!confirm('Discard your edits and revert to the last saved version?')) return;
    textarea.value = originalSource;
    patchList.replaceChildren();
    aiStatus.textContent = '';
    setDirty();
  });

  // ---- AI patch flow ----
  applyBtn.addEventListener('click', () => { void runAiPatch(); });

  async function runAiPatch(): Promise<void> {
    const request = promptInput.value.trim();
    if (!request) {
      aiStatus.textContent = 'Type what you want changed first.';
      return;
    }
    if (!getWalletState().connection) {
      aiStatus.innerHTML = 'Turn on AI in <a href="#/settings?focus=wallet">Settings</a> first.';
      return;
    }
    applyBtn.disabled = true;
    promptInput.disabled = true;
    aiStatus.textContent = 'Asking the model…';

    try {
      const profile = await readProfile(root);
      const jobTxt = await readJobTxt(root, app);

      const result = await requestPatch({
        kind,
        source: currentSource,
        profileText: profile?.rawText ?? '',
        jobTitle: app.jobTitle,
        companyName: app.companyName,
        jobUrl: app.jobUrl,
        jobDescription: jobTxt?.description ?? '',
        userRequest: request,
      });

      if (!result.ok) {
        aiStatus.textContent = `Failed at "${result.stage}": ${result.message}`;
        return;
      }

      if (result.patch.edits.length === 0) {
        aiStatus.textContent = result.patch.note
          ? `No changes proposed. ${result.patch.note}`
          : 'No changes proposed.';
        return;
      }

      const applied = applyPatch(currentSource, result.patch);
      const successCount = applied.edits.filter((e) => e.applied).length;
      currentSource = applied.output;
      textarea.value = applied.output;
      setDirty();

      aiStatus.textContent = result.patch.note
        ? `Applied ${successCount} of ${applied.edits.length} edits. ${result.patch.note}`
        : `Applied ${successCount} of ${applied.edits.length} edits.`;

      patchList.replaceChildren(
        ...applied.edits.map((edit, i) =>
          renderPatchCard({
            edit,
            index: i,
            onUndo: (index) => undoEdit(applied.edits, index),
          }),
        ),
      );

      promptInput.value = '';

      // Recompile so the preview reflects the AI's edits.
      if (successCount > 0) void refreshPreview();
    } catch (err) {
      aiStatus.textContent = `Unexpected: ${err instanceof Error ? err.message : String(err)}`;
    } finally {
      applyBtn.disabled = false;
      promptInput.disabled = false;
    }
  }

  function undoEdit(edits: AppliedEdit[], index: number): void {
    const edit = edits[index];
    if (!edit || !edit.applied) return;
    // Undo means: in the *current* source, find `replace` and substitute back
    // `find`. We require uniqueness, just like the apply path.
    const first = currentSource.indexOf(edit.replace);
    if (first === -1) {
      aiStatus.textContent = 'Could not undo: the replaced text is no longer in the source (further edits on top).';
      return;
    }
    const second = currentSource.indexOf(edit.replace, first + 1);
    if (second !== -1) {
      aiStatus.textContent = 'Could not undo: the replaced text now appears multiple times.';
      return;
    }
    currentSource = currentSource.slice(0, first) + edit.find + currentSource.slice(first + edit.replace.length);
    textarea.value = currentSource;
    edit.applied = false;
    edit.reason = 'Reverted';
    setDirty();
    // Re-render the affected card in place.
    patchList.replaceChildren(
      ...edits.map((e, i) =>
        renderPatchCard({
          edit: e,
          index: i,
          onUndo: (idx) => undoEdit(edits, idx),
        }),
      ),
    );
  }

  // ---- Lifecycle ----
  function attemptClose(): void {
    if (saving) return;
    const dirty = textarea.value !== originalSource;
    if (dirty && !confirm('You have unsaved changes. Close anyway?')) return;
    cleanup();
  }

  function cleanup(): void {
    document.removeEventListener('keydown', onKey);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      previewUrl = null;
    }
    overlay.remove();
    document.body.classList.remove('doc-editor-open');
  }

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      attemptClose();
      return;
    }
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
      e.preventDefault();
      if (!saveBtn.disabled) void saveAndCompile();
    }
  }

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) attemptClose();
  });
  document.addEventListener('keydown', onKey);

  document.body.classList.add('doc-editor-open');
  document.body.appendChild(overlay);
  // Start focus on the AI prompt (the primary way to change the document now).
  queueMicrotask(() => {
    if (!promptInput.disabled) promptInput.focus();
  });
}
